import { useTranslation } from "@sagot/i18n";
import { CopyButton, ErrorState, Skeleton } from "@sagot/ui";
import { useHookSpec } from "../use-workflows-p4";

const H = "engage.workflows.hook";
const BYTES_PER_KB = 1024;
const SECONDS_PER_MINUTE = 60;

/** How the client's system calls a run's signed URL (from `GET /v1/workflows/hook-spec`): the URL pattern, the headers and how to sign. */
export function HookInstructions() {
  const { t } = useTranslation();
  const spec = useHookSpec();
  if (spec.isError) return <ErrorState title={t(`${H}.specFailed`)} onRetry={() => void spec.refetch()} retryLabel={t("common.retry")} />;
  if (spec.isPending) return <Skeleton className="h-40 rounded-md" aria-hidden="true" />;
  const { data } = spec;
  return (
    <section className="space-y-3 rounded-md border border-line bg-surface-2 p-4" aria-labelledby="hook-howto" data-testid="hook-instructions">
      <h3 id="hook-howto" className="text-sm font-bold">{t(`${H}.howTitle`)}</h3>
      <p className="text-xs text-fg-muted">{t(`${H}.howBody`)}</p>
      <div>
        <p className="label">{t(`${H}.urlPattern`)}</p>
        <div className="flex items-center gap-1 rounded-md bg-surface px-3 py-2">
          <code className="min-w-0 flex-1 break-all font-mono text-xs">{`${data.method} ${data.url_pattern}`}</code>
          <CopyButton value={data.url_pattern} label={t(`${H}.copyPattern`)} copiedLabel={t(`${H}.copied`)} />
        </div>
      </div>
      <ol className="list-decimal space-y-1.5 pl-5 text-xs text-fg-muted">
        <li>{t(`${H}.stepSecret`)}</li>
        <li>{t(`${H}.stepSign`, { header: data.signature_header })}</li>
        <li>{t(`${H}.stepSend`, { limit: Math.round(data.max_body_bytes / BYTES_PER_KB), keys: data.max_keys, chars: data.max_value_chars })}</li>
      </ol>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- the signature line scrolls sideways on a phone: keyboard users must be able to focus it (axe scrollable-region-focusable) */}
      <pre tabIndex={0} role="group" className="code overflow-x-auto text-xs" aria-label={t(`${H}.signatureLabel`)}><code>{`${data.signature_header}: ${data.signature_format}`}</code></pre>
      <p className="text-xs text-fg-subtle">{t(`${H}.tolerance`, { minutes: Math.round(data.tolerance_seconds / SECONDS_PER_MINUTE), header: data.idempotency_header })}</p>
    </section>
  );
}
